import { useApi } from '@backstage/core-plugin-api';
import { Progress, UserIdentity } from '@backstage/core-components';
import type { SignInPageProps } from '@backstage/core-plugin-api';
import { Button, makeStyles, Typography } from '@material-ui/core';
import { useEffect, useState } from 'react';
import { keycloakAuthApiRef } from '../../keycloak';
import { LogoFull } from '../nav/LogoFull';

const useStyles = makeStyles(theme => ({
  root: {
    height: '100vh',
    width: '100vw',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.background.paper} 100%)`,
  },
  card: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(3),
    padding: theme.spacing(6, 5),
    borderRadius: theme.shape.borderRadius * 2,
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[8],
    minWidth: 340,
  },
  subtitle: {
    color: theme.palette.text.secondary,
    textAlign: 'center',
  },
  button: {
    minWidth: 220,
    padding: theme.spacing(1.5, 3),
    borderRadius: theme.shape.borderRadius * 4,
    textTransform: 'none',
    fontWeight: 600,
  },
  error: {
    color: theme.palette.error.main,
    textAlign: 'center',
  },
}));

export const CustomSignInPage = (props: SignInPageProps) => {
  const classes = useStyles();
  const authApi = useApi(keycloakAuthApiRef);
  const [checking, setChecking] = useState(true);
  const [signingIn, setSigningIn] = useState(false);
  const [error, setError] = useState<Error>();

  useEffect(() => {
    let cancelled = false;
    authApi
      .getBackstageIdentity({ optional: true })
      .then(async identity => {
        if (!identity || cancelled) {
          return;
        }
        const profile = await authApi.getProfile();
        props.onSignInSuccess(
          UserIdentity.create({
            identity: identity.identity,
            authApi,
            profile,
          }),
        );
      })
      .finally(() => {
        if (!cancelled) {
          setChecking(false);
        }
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSignIn = async () => {
    setSigningIn(true);
    setError(undefined);
    try {
      const identity = await authApi.getBackstageIdentity({
        instantPopup: true,
      });
      if (!identity) {
        throw new Error('Keycloak sign-in was not completed');
      }
      const profile = await authApi.getProfile();
      props.onSignInSuccess(
        UserIdentity.create({ identity: identity.identity, authApi, profile }),
      );
    } catch (err) {
      setError(err as Error);
      setSigningIn(false);
    }
  };

  if (checking) {
    return <Progress />;
  }

  return (
    <div className={classes.root}>
      <div className={classes.card}>
        <LogoFull />
        <Typography variant="h5" component="h1">
          Ekimetrics Developer Portal
        </Typography>
        <Typography variant="body2" className={classes.subtitle}>
          Sign in with your Ekimetrics account to continue
        </Typography>
        <Button
          className={classes.button}
          variant="contained"
          color="primary"
          disabled={signingIn}
          onClick={handleSignIn}
        >
          {signingIn ? 'Signing in…' : 'Sign in with Ekimetrics SSO'}
        </Button>
        {error && (
          <Typography variant="body2" className={classes.error}>
            {error.message}
          </Typography>
        )}
      </div>
    </div>
  );
};
